import React from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Worker, Viewer } from "@react-pdf-viewer/core";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const ViewerSkeleton = () => {
  return (
    <Box sx={{ p: 2 }}>
      <Skeleton variant="text" width="35%" height={28} />
      <Skeleton variant="text" width="55%" height={20} sx={{ mb: 2 }} />
      <Skeleton
        variant="rounded"
        width="100%"
        height={520}
        sx={{ borderRadius: 2 }}
      />
    </Box>
  );
};

const EmptyViewer = () => {
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        color: "text.secondary",
        gap: 1,
      }}
    >
      <InsertDriveFileOutlinedIcon sx={{ fontSize: 42, opacity: 0.6 }} />
      <Typography variant="body1">Select a document to view</Typography>
    </Box>
  );
};

const DocumentViewerPanel = ({
  selectedDoc,
  documentUrl,
  loadingDoc,
  defaultLayoutPluginInstance,
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        p: 2,
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      {loadingDoc ? (
        <ViewerSkeleton />
      ) : selectedDoc && documentUrl ? (
        <>
          <Card
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography fontWeight={600} fontSize={15}>
              {selectedDoc?.filetitle || selectedDoc?.filename}
            </Typography>
            <Typography color="text.secondary" fontSize={13} mt={0.5}>
              {selectedDoc?.filedescription ||
                selectedDoc?.notes ||
                selectedDoc?.filename}
            </Typography>
          </Card>

          <Box
            sx={{
              height: "calc(70vh - 110px)",
              overflow: "auto",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {documentUrl?.mimeType?.includes("pdf") ? (
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={documentUrl.url}
                  plugins={[defaultLayoutPluginInstance]}
                />
              </Worker>
            ) : documentUrl?.mimeType?.startsWith("image/") ? (
              <img
                src={documentUrl.url}
                alt={selectedDoc?.filetitle || "Document preview"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box textAlign="center" p={3}>
                <Typography color="text.secondary" mb={1}>
                  Unsupported file type: {documentUrl?.mimeType || "unknown"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please download or open this file in a new tab.
                </Typography>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <EmptyViewer />
      )}

      {loadingDoc && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
    </Paper>
  );
};

export default DocumentViewerPanel;
